/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { getConsoleService } from '../../Services/console';
import { SessionManagerProps } from './SessionManager.props';

/**
 * DXP Console - Session Manager.
 * @param {SessionManagerProps} props The props for `<SessionManager />`.
 * @export
 * @returns {JSX.Element} The `<SessionManager />` component.
 */
export const SessionManager = (props: SessionManagerProps): JSX.Element => {
  const navigate = useNavigate();
  const { children, location } = props;

  // Console service
  const consoleService = getConsoleService();

  useEffect((): void => {
    // Check the DXP Console health endpoint via Cloudflare
    // which enables us to see whether our session is still valid.
    lastValueFrom(consoleService.getHealth())
      .then(() => {
        // The session is valid, and nothing further needs to be done.
        // If the session was close to expiring, it should have
        // been automatically refreshed by the Cloudflare worker.
        return true;
      })
      .catch((error) => {
        // The session is assumed to have expired, as there are
        // no permission checks for the health endpoint.

        /* eslint-disable no-console */
        console.error(`Invalid session`);
        console.error(error);
        /* eslint-enable */

        // Log out the user to force a sign-in process.
        navigate({ pathname: `/logout` });

        return true;
      });
  }, [consoleService, location, navigate]);

  return <>{children}</>;
};
